import { ReactNode, useMemo, useState } from "react";
import {
  Button,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
} from "reactstrap";
import { CheckTreePicker } from "rsuite";
import pluralize from "pluralize";
import dayjs from "dayjs";
import { TreeNode } from "rsuite/esm/internals/Tree/types";

import AppDrawer from "./AppDrawer";
import JSONTextEditor from "../JSONTextEditor/JSONTextEditor";
import useExportData from "../../api/api-hooks/records/useExportData";
import CustomToggle from "./Fields/CustomToggle";
import useModule from "../../hooks/useModule";

type FileTypes = "xlsx" | "csv";
type ExportFieldsType = "table" | "all" | "custom";

interface Props {
  open: boolean;
  moduleAPIName: string;
  onClose: () => void;
  query: any;
  recordIDs: string[];
  multiSubitemField?: string;
  parentRecordID?: string;
  isSQLQuery?: boolean;
}

const emptyFilter = "No filters have been added.";

const fileTypeMap: { [key in FileTypes]: string } = {
  xlsx: "Excel File",
  csv: "CSV File",
};

const DataExportDrawer = ({
  open,
  moduleAPIName,
  onClose,
  query,
  multiSubitemField,
  parentRecordID,
  recordIDs = [],
  isSQLQuery = false,
}: Props) => {
  const headerTitle = "Bulk Export Records";
  const headerDescription = "Export records to Excel or CSV";
  const [fileName, setFileName] = useState(
    `${moduleAPIName ?? ""}Export_${dayjs().format("YYYY_MM_DD_HHmmSS")}`
  );
  const [fileType, setFileType] = useState<FileTypes>("xlsx");
  const [exportFields, setExportFields] = useState<ExportFieldsType>("table");
  const [growthCloudExport, setGrowthCloudExport] = useState(true);
  const [customFields, setCustomFields] = useState<string[]>();
  const { exportData } = useExportData();
  const { module } = useModule(moduleAPIName);

  const handleClose = () => {
    onClose();
    setFileName("");
    setFileType("xlsx");
    setGrowthCloudExport(true);
  };

  const handleExportData = async () => {
    // If user selected "Fields from table" just use the existing query.
    // If user selected "All fields in {moduleAPIName" overwrite the queries fields.
    const finalQuery =
      exportFields === "table"
        ? query
        : exportFields === "custom"
        ? { ...query, Fields: customFields }
        : { ...query, Fields: ["*"] };

    await exportData({
      FileName: fileName,
      Query: finalQuery,
      SQLQuery: isSQLQuery,
      Records: recordIDs,
      moduleAPIName,
      FileType: fileType,
      MultiSubitemField: multiSubitemField,
      ParentRecordID: parentRecordID,
      GrowthCloudExport: growthCloudExport,
    });

    onClose();
  };

  const availableFields: TreeNode[] | null = useMemo(() => {
    if (!module) return null;

    const fields = module?.Fields ?? [];
    const data = [];

    data.push({
      value: "id",
      label: "id",
    });
    data.push({
      value: "_RecordName",
      label: "_RecordName",
    });

    for (const field of fields) {
      if (!field?.FieldType || ["id", "_RecordName"].includes(field.APIName))
        continue;
      const optionData: TreeNode = {
        value: field.APIName,
        label: field.Name,
      };

      if (field?.SubitemFields) {
        optionData.children = field.SubitemFields.map((subField) => ({
          value: `${field.APIName}.${subField.Name}`,
          label: `${field.Name} - ${subField.Name}`,
        }));
      }
      data.push(optionData);
    }

    return data;
  }, [module]);

  return (
    <AppDrawer
      open={open}
      placement="right"
      size="md"
      backdrop="static"
      onClose={handleClose}
      headerTitle={headerTitle}
      headerDescription={headerDescription}
      info={`Exporting ${
        isSQLQuery ? "all the" : recordIDs?.length
      } ${pluralize("records", recordIDs?.length)} as ${fileTypeMap[fileType]}`}
      headerOptions={
        <Button
          size="sm"
          style={{ width: 80 }}
          color="primary"
          onClick={handleExportData}
        >
          Export
        </Button>
      }
      body={
        <div className="mb-4 mt--2">
          <DataExportFormGroup
            title="File Type"
            description="Select the name of the generated file."
          >
            <Input
              type="select"
              onChange={(e) => setFileType(e?.target?.value as FileTypes)}
              value={fileType}
            >
              <option value="" disabled>
                Select a File Type
              </option>
              <option value="xlsx">{fileTypeMap["xlsx"]}</option>
              <option value="csv">{fileTypeMap["csv"]}</option>
            </Input>
          </DataExportFormGroup>
          <DataExportFormGroup
            title="File Name"
            description="Enter a name for your exported record file."
          >
            <InputGroup>
              <Input
                value={fileName}
                onChange={(e) => setFileName(e.target.value.replace(/\s/g, ""))}
              />
              <InputGroupAddon addonType="append">
                <InputGroupText className="text-lowercase text-dark">
                  .{fileType}
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </DataExportFormGroup>
          <DataExportFormGroup
            title="Export Fields"
            description="Select the fields to include in your export."
          >
            <Input
              type="select"
              onChange={(e) =>
                setExportFields(e?.target?.value as ExportFieldsType)
              }
              disabled={isSQLQuery}
              value={exportFields}
            >
              <option value="" disabled>
                Select Export Fields
              </option>
              <option value="table">Fields from table</option>
              <option value="all">All fields in {moduleAPIName}</option>
              <option value="custom">Custom fields</option>
            </Input>
          </DataExportFormGroup>
          {exportFields === "custom" && (
            <DataExportFormGroup
              title="Custom Fields"
              description="Select the fields to include in your export."
            >
              <CheckTreePicker
                preventOverflow
                className="w-100"
                // @ts-ignore
                onChange={(value) => setCustomFields((prev) => value)}
                data={availableFields || []}
              />
            </DataExportFormGroup>
          )}
          <DataExportFormGroup
            title="GrowthCloud Export"
            description="When enabled, the export will be in a format that can be imported back into GrowthCloud."
          >
            <CustomToggle
              value={growthCloudExport}
              handleChange={() => setGrowthCloudExport((prev) => !prev)}
            />
          </DataExportFormGroup>
          <h4 className="mb-0">Data Source</h4>
          <div className="small mb-4">{moduleAPIName}</div>
          {!isSQLQuery && (
            <>
              <h4 className="mb-2">Filter</h4>
              {/* @ts-ignore */}
              <JSONTextEditor
                readOnly
                existingObject={
                  multiSubitemField
                    ? query?.SubQueries?.[0].Query.Filters?.length > 0
                      ? query.SubQueries[0].Query.Filters
                      : emptyFilter
                    : query?.Filters && query?.Filters?.length > 0
                    ? query?.Filters
                    : emptyFilter
                }
              />
            </>
          )}
        </div>
      }
    />
  );
};

function DataExportFormGroup({ title, description, children }: FormGroupProps) {
  return (
    <FormGroup>
      <h4 className="mb-0">{title}</h4>
      <div className="small mb-2">{description}</div>
      {children}
    </FormGroup>
  );
}

interface FormGroupProps {
  title: string;
  description: string;
  children: ReactNode;
}

export default DataExportDrawer;
